/**
 * @typedef {'ONLINE'|'OFFLINE'|'UNKNOWN'} OnuStatus
 * @typedef {'UP'|'DOWN'|'UNKNOWN'} PppoeStatus
 * @typedef {'NORMAL'|'TOTAL_DOWN'|'ONU_UP_PPP_DOWN'|'FLAPPING'} CorrelationState
 * @typedef {'IDLE'|'QUEUED'|'RUNNING'|'SUCCESS'|'FAILED'|'ESCALATED'} RecoveryStatus
 *
 * @typedef {Object} CustomerConnectionMonitor
 * @property {string} customerId
 * @property {string} customerName
 * @property {string} areaName
 * @property {string=} odpName
 * @property {{serialNumber:string,status:OnuStatus,oltName?:string,ponPort?:string,onuId?:string,opticalPower?:number|null,lastSeenAt?:string}} onu
 * @property {{username:string,status:PppoeStatus,nasName?:string,ipAddress?:string|null,uptimeSec?:number|null,lastSeenAt?:string|null,disconnectReason?:string|null}} pppoe
 * @property {{state:CorrelationState,detectedAt:string,durationSec:number,confidence?:number}} correlation
 * @property {{eligible:boolean,status:RecoveryStatus,retryCount:number,maxRetry:number,nextRecheckAt?:string|null,waSent:boolean,ticketId?:string|null}} recovery
 * @property {{lat:number,lng:number}=} location
 */

export const normalizeOnuStatus = (raw) => {
  const value = String(raw || '').trim().toLowerCase();
  if (value === 'online') return 'ONLINE';
  if (value === 'offline') return 'OFFLINE';
  return 'UNKNOWN';
};

export const normalizePppoeStatus = (raw) => {
  const value = String(raw || '').trim().toLowerCase();
  if (value === 'online' || value === 'up') return 'UP';
  if (value === 'offline' || value === 'down') return 'DOWN';
  return 'UNKNOWN';
};

export const deriveCorrelationState = (onuStatus, pppStatus, isFlapping = false) => {
  if (isFlapping) return 'FLAPPING';
  if (onuStatus === 'ONLINE' && pppStatus === 'UP') return 'NORMAL';
  if (onuStatus === 'OFFLINE' && pppStatus === 'DOWN') return 'TOTAL_DOWN';
  if (onuStatus === 'ONLINE' && pppStatus === 'DOWN') return 'ONU_UP_PPP_DOWN';
  return 'FLAPPING';
};

export const getCorrelationBadgeClass = (state) => {
  const clean = String(state || '').toUpperCase();
  if (clean === 'NORMAL') return semanticToneToBadgeClass('ok');
  if (clean === 'TOTAL_DOWN') return semanticToneToBadgeClass('danger');
  if (clean === 'ONU_UP_PPP_DOWN' || clean === 'FLAPPING') return semanticToneToBadgeClass('warn');
  return semanticToneToBadgeClass('neutral');
};

export const getRecoveryBadgeClass = (status) => {
  const clean = String(status || '').toUpperCase();
  if (clean === 'RUNNING' || clean === 'QUEUED') return semanticToneToBadgeClass('info');
  if (clean === 'SUCCESS') return semanticToneToBadgeClass('ok');
  if (clean === 'FAILED' || clean === 'ESCALATED') return semanticToneToBadgeClass('danger');
  return semanticToneToBadgeClass(mapMonitoringStatusToTone(clean));
};

export const getCorrelationMapColor = (state, recoveryStatus) => {
  const recovery = String(recoveryStatus || '').toUpperCase();
  if (recovery === 'RUNNING' || recovery === 'QUEUED') return semanticToneToColor('info');
  if (recovery === 'ESCALATED') return semanticToneToColor('danger');
  const clean = String(state || '').toUpperCase();
  if (clean === 'NORMAL') return semanticToneToColor('ok');
  if (clean === 'TOTAL_DOWN') return semanticToneToColor('danger');
  if (clean === 'ONU_UP_PPP_DOWN' || clean === 'FLAPPING') return semanticToneToColor('warn');
  return semanticToneToColor('neutral');
};

export const formatDurationSec = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h <= 0) return `${m}m`;
  return `${h}j ${m}m`;
};

const hashString = (value) => {
  return String(value || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
};

export const buildMockRecoveryByCorrelation = (customerId, correlationState) => {
  const seed = hashString(customerId);
  if (correlationState === 'NORMAL') {
    return {
      eligible: false,
      status: 'IDLE',
      retryCount: 0,
      maxRetry: 2,
      nextRecheckAt: null,
      waSent: false,
      ticketId: null,
    };
  }

  if (correlationState === 'ONU_UP_PPP_DOWN') {
    const rolling = seed % 3;
    if (rolling === 0) {
      return {
        eligible: true,
        status: 'RUNNING',
        retryCount: 1,
        maxRetry: 2,
        nextRecheckAt: new Date(Date.now() + 75 * 1000).toISOString(),
        waSent: false,
        ticketId: null,
      };
    }
    if (rolling === 1) {
      return {
        eligible: true,
        status: 'FAILED',
        retryCount: 2,
        maxRetry: 2,
        nextRecheckAt: null,
        waSent: true,
        ticketId: null,
      };
    }
    return {
      eligible: true,
      status: 'ESCALATED',
      retryCount: 2,
      maxRetry: 2,
      nextRecheckAt: null,
      waSent: true,
      ticketId: `TKT-${seed}`,
    };
  }

  if (correlationState === 'TOTAL_DOWN') {
    return {
      eligible: false,
      status: 'ESCALATED',
      retryCount: 0,
      maxRetry: 2,
      nextRecheckAt: null,
      waSent: true,
      ticketId: `TKT-${seed}`,
    };
  }

  return {
    eligible: true,
    status: 'QUEUED',
    retryCount: 0,
    maxRetry: 2,
    nextRecheckAt: new Date(Date.now() + 90 * 1000).toISOString(),
    waSent: false,
    ticketId: null,
  };
};
import {
  mapMonitoringStatusToTone,
  semanticToneToBadgeClass,
  semanticToneToColor,
} from '../features/ui-system/status/semanticStatus';
